import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";
import dayjs from "dayjs";

import SvgLoadingAnimation from "../../../../components/loading";
import IconButton from "../../../../components/button/IconButton";
import DropdownMenu, {
  type DropdownMenuItem,
} from "../../../../components/dropdown-menu/DropdownMenu";
import ProjectNavLinks from "../../../../components/project/ProjectNavLinks";
import ConfirmModal from "../../../../components/modal/ConfirmModal";
import Table, { TableColumn } from "../../../../components/table/Table";
import {
  ProducerTask,
  TaskBoard,
  TaskColumn,
  TaskCell,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useMoveTaskMutation,
  useUpdateColumnsMutation,
  type CreateTaskParams,
  type DeleteTaskParams,
  type UpdateColumnsParams,
  type UpdateTaskParams,
} from "../../../../app/services/taskBoard";
import {
  useGetShotsQuery,
  useUpdateShotMutation,
  type Shot,
} from "../../../../app/services/shots";
import { useGetAllProjectMembershipsQuery } from "../../../../app/accounts/memberships";
import {
  getShotTableStatusKey,
  isDeliveryDateEditable,
} from "../../../../constant/shotTableStatus";
import { toDeliveryDateParam } from "../../../home/components/ShotTable/shotTableUtils";
import { useGetDmThreadsQuery } from "../../../../app/services/dmThreads";
import type { StatusType } from "../../../../components/status-indicator/StatusIndicator";
import TaskBoardRow from "./TaskBoardRow";
import TaskColumnHeader from "./TaskColumnHeader";
import TaskBoardSyncStatus from "./TaskBoardSyncStatus";
import TaskMoveModal, { type TaskMoveTargetBoard } from "./TaskMoveModal";
import { useTaskBoardPresence } from "./useTaskBoardPresence";
import {
  buildBoardTsv,
  CB_AUTO_MARKER_KEY,
  computeTaskLatestDate,
  computeTaskRightmostDate,
  filterColumnsByMentionContent,
  findOfferStatusLabel,
  groupColumnsByParallel,
  hasCbAutoMarker,
  isTabularClipboardText,
  isTaskCellsEmpty,
  isTaskComplete,
  isTaskFilterActive,
  isTaskHeaderRow,
  matchesCompletionFilter,
  parseClipboardTable,
  planTaskMove,
  sortTasksByColumn,
  sortTasksByCompletionThenDate,
  splitTaskByLines,
  TASK_SORT_ARROW_ICON,
  taskHasDateInRange,
  taskMatchesMentions,
  withoutCbAutoMarker,
  type TaskCompletionFilter,
  type TaskDateRange,
  type TaskSortState,
} from "./taskBoardUtils";
import { normalizeNfc } from "../../../../utils/text";
import { ShotStatus } from "../../../../types/home";
import {
  COLUMN_LABEL_COLOR_RATIO,
  mixWithWhite,
} from "../../../taskboard/components/activityChartStyle";

// ショット名照合用にまとめて取得する件数の上限。
const SHOT_LOOKUP_LIMIT = 200;

// 段階描画の行数。取得済みデータのうち「描く行数」だけを制御するもので、
// 見える範囲（どこまで目に入るか）は表の高さ側が担う。
/** 初期描画に必ず含める行数の下限 */
const ROW_RENDER_INITIAL = 30;
/** 上スクロールで一度に継ぎ足す行数 */
const ROW_RENDER_CHUNK = 30;

/**
 * 「完了」「すべて」表示で表の中を縦スクロールさせるときの本文の最低高さ(px)。
 *
 * 高さは「未完了の全行 + 直近の完了 1 行」の実測に合わせるが、未完了が 1 行も
 * 無い「完了」表示では 1 行分の高さになってしまうため下限を置く。
 */
const MIN_SCROLL_BODY_HEIGHT = 320;

// @メンションのオファーステータス表示用に取得するオファー件数の上限。
const OFFER_LOOKUP_LIMIT = 100;

const TITLE_COL_KEY = "__title";
const ACTIONS_COL_KEY = "__actions";

/**
 * カード外枠。`$bannerColor` 指定時はプロジェクトバナーと同じ
 * `banner-color${N}` クラスを当て、ヘッダー帯にカラー（と白文字）を表現する。
 */
const SectionRoot = styled.div.attrs<{ $bannerColor?: string }>(
  ({ $bannerColor }) => ({
    className: $bannerColor ? `banner-color${$bannerColor}` : undefined,
  }),
)<{ $bannerColor?: string }>`
  width: 100%;
  background: ${({ $bannerColor }) => ($bannerColor ? undefined : "#fff")};
  border: 1px solid var(--soremo-border);
  border-radius: 12px;
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
  padding: 0;
  overflow: hidden;
`;

/**
 * ヘッダー帯。バナーカラー上では文字・アイコン色を `banner-color${N}` の div 色
 * （背景の濃淡に応じた白／黒）に委ねる。白固定にすると薄いバナーで読めなくなるため、
 * `color: inherit` で SectionRoot 由来の色を継承する。
 */
const SectionHeader = styled.div<{ $onBanner: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  ${({ $onBanner }) =>
    $onBanner
      ? `
    & h3, & span, & button { color: inherit; }
    & .material-symbols-rounded { color: inherit; }
  `
      : ""}
`;

/**
 * 表本体を白カードとして内側に重ね、`.banner-color${N}` 由来の
 * `div { color: white }` が表のテキストに継承されないよう色をリセットする。
 * `$onBanner` のときは外周にバナーカラーが「枠」として見えるよう
 * 左右・下に余白（margin）を取り、内側カードを角丸で切り抜く。
 *
 * 色リセットは `&&`（自クラス2回）で詳細度を (0,2,0) / (0,2,1) に上げる。
 * `.banner-colorN div`（0,1,1）は SectionBody 自身にもマッチして白文字にする
 * ため、単一クラス（0,1,0）の指定では負けてしまう。従来は Quirks Mode の
 * 「tables inherit color from body quirk」で偶然表内が黒く見えていたが、
 * 標準モード（DOCTYPE あり）では table も親の色を継承するため、
 * ここで確実に勝たせる必要がある。
 */
const SectionBody = styled.div<{ $onBanner: boolean }>`
  background: #fff;
  && {
    color: var(--soremo-black, #000);
  }
  ${({ $onBanner }) =>
    $onBanner
      ? `
    margin: 0 8px 8px;
    border-radius: 8px;
    padding: 12px 16px 16px;
  `
      : `
    padding: 0 16px 16px;
  `}
  /* バナーカラー（外枠）の白文字ルールがここから先に降りてこないよう div を上書き。
     ただしアイコンフォントは自前の色（と hover）を持つため対象外にする。
     ここで巻き込むと 3 点リーダー等がコンポーネント側の指定を失う。 */
  && div:not(.material-symbols-rounded) {
    color: inherit;
  }
`;

/** プロジェクトトップを別タブで開くヘッダーリンク（コードネーム + ボード名）。 */
const HeaderTitleLink = styled(Link).attrs({
  className: "u-row u-items-center u-gap4",
})`
  color: inherit;
  text-decoration: none;
  /* flex 子として縮められるようにする（長いコードネームで右側の件数・
     ボタン群を押し出さない） */
  min-width: 0;
`;

const BulkOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 2;
`;

/** 縦積みモードのヘッダー: 並列グループ内の各列ヘッダーを縦に並べる */
const StackedHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

/**
 * 縦積みヘッダー内の 1 列分。列（工程）に配色があるときだけ淡い背景で塗る
 * （通常の横並び時は th 全体を `headerStyle` で塗るのと同じ見せ方）。
 */
const StackedHeaderItem = styled.div<{ $background?: string }>`
  ${({ $background }) =>
    $background
      ? `background: ${$background}; border-radius: 4px; padding: 0 4px;`
      : ""}
`;

const ADD_INPUT_STYLE: React.CSSProperties = {
  flex: 1,
  padding: "1px 6px",
  fontSize: 13,
  lineHeight: "150%",
  height: 24,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

const genColumnId = () =>
  `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** 編集中ユーザーなしの共有戻り値（毎回の新規配列生成を避ける） */
const EMPTY_EDITOR_NAMES: readonly string[] = [];

interface TaskBoardSectionProps {
  projectId: string;
  projectName: string;
  /**
   * ヘッダーのプロジェクト名の隣に添えるボード名。フィルター適用中に
   * 複数ボードを並べて表示するとき、どのボードの表かを見分けるために渡す
   *（通常表示ではタブが役割を担うので省略）。
   */
  boardLabel?: string;
  /**
   * 表示するボード。取得は親（ProjectTaskBoards）が `GET /task-boards` で
   * まとめて行い、ここは受け取った 1 枚を描くことに専念する。
   */
  board: TaskBoard;
  /** 対象ボードの ID（タスク・列の更新先）。 */
  boardId: string;
  /** 保存に失敗したときにサーバーの状態へ巻き戻すための再取得ハンドラ。 */
  onRefetchBoard: () => void;
  /** 親がボードを再取得中か（テーブルに読み込みオーバーレイを出す）。 */
  isRefreshing?: boolean;
  /**
   * やることリスト（チェックバック中ショット）の自動行追加と、その後始末を
   * このボードで行うか。既定 true。複数ボードのプロジェクトでは一番左の
   * ボードにだけ true を渡し、同じ行が各ボードへ増えるのを防ぐ。
   */
  enableShotAutoAdd?: boolean;
  /** プロジェクトバナーのカラー番号（`banner-color${N}` クラス対応）。
   *  指定があればカード全体の背景に同じカラーセットを適用する。 */
  bannerColor?: string;
  /** タスクボードの編集権限（一覧 API の `task_board_access === "edit"` 由来）。
   *  false のときは読み取り専用（列追加・タスク編集・貼り付け等を無効化）。 */
  canEdit: boolean;
  /** 並列工程を縦積み表示するか（横断ビュー一括の設定）。false=従来の横並び */
  stackParallel?: boolean;
  /**
   * 並列工程の縦積み ⇄ 横並びを切り替える（ラベル行右端の 3 点リーダー
   * メニューの項目）。未指定ならメニューに出さない。
   */
  onToggleStackParallel?: () => void;
  /** 完了フィルター（横断ビューの一括切り替え由来）。既定は未完了のみ */
  completionFilter?: TaskCompletionFilter;
  /** 担当者フィルター（本文の @表示名。複数 = OR）。空なら絞り込みなし */
  selectedMentions: string[];
  /** タスク名フィルター（部分一致・大文字小文字無視）。空なら絞り込みなし */
  taskNameFilter?: string;
  /** 日付範囲フィルター（日付チップ由来）。null なら絞り込みなし */
  dateRange?: TaskDateRange | null;
  /** `@表示名` → チェックボックス色（アクティビティ集計のユーザー配色） */
  // eslint-disable-next-line no-unused-vars
  mentionColorOf?: (name: string) => string | undefined;
  /** 列（工程）名 → 色（アクティビティ集計の項目配色）。列ラベルの背景に使う */
  // eslint-disable-next-line no-unused-vars
  columnColorOf?: (columnTitle: string) => string | undefined;
  /** 自分のポジション（一覧 API の `current_user_role` を渡す）。見出しの導線アイコンの出し分けに使う */
  position: string;
  /** 納品物なし（total_shots_count === 0）。導線アイコンにホームを出さない */
  noDeliverable: boolean;
  /** 保存処理の開始通知（横断ビューの StatusIndicator 用） */
  onSaveStart?: () => void;
  /** 保存処理の終了通知（`ok=false` で失敗扱い） */
  // eslint-disable-next-line no-unused-vars
  onSaveEnd?: (ok: boolean) => void;
  /** 最後にボードを取得できた時刻（RTK Query の fulfilledTimeStamp, ms） */
  lastSyncedAt?: number;
  /** このプロジェクトのボード群の保存状態（右下の同期バーに表示） */
  saveStatus?: StatusType;
  /**
   * 行の移動先にできる同一プロジェクト内の別ボード（編集できるもののみ）。
   * 空なら行メニューの「行を移動」を disabled にする。
   */
  moveTargetBoards?: TaskMoveTargetBoard[];
}

const TaskBoardSection: React.FC<TaskBoardSectionProps> = ({
  projectId,
  projectName,
  boardLabel,
  board: activeBoard,
  boardId,
  onRefetchBoard: refetch,
  isRefreshing = false,
  enableShotAutoAdd = true,
  bannerColor,
  canEdit,
  stackParallel = false,
  onToggleStackParallel,
  completionFilter = "incomplete",
  selectedMentions,
  taskNameFilter = "",
  dateRange = null,
  mentionColorOf,
  columnColorOf,
  position,
  noDeliverable,
  onSaveStart,
  onSaveEnd,
  lastSyncedAt,
  saveStatus = "idle",
  moveTargetBoards = [],
}) => {
  const navigate = useNavigate();

  // ── セル単位の編集中 presence（typing.task_board.*） ──
  // 他ユーザーが編集中のセルへ「◯◯が編集中」バッジを出す。ユーザー名は
  // presence が実際に発生したときだけメンバー一覧を取得して解決する
  // （横断ビューでプロジェクト数ぶんの一覧取得が常時走るのを避ける）。
  const { resolveCellEditors, hasRemoteEditors } =
    useTaskBoardPresence(projectId);
  const { data: membershipsData } = useGetAllProjectMembershipsQuery(
    { projectId },
    { skip: !projectId || !hasRemoteEditors },
  );
  const memberNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const member of membershipsData?.data ?? []) {
      const name = member.display_name_jp || member.display_name_en || "";
      if (name && !map.has(member.user_id)) map.set(member.user_id, name);
    }
    return map;
  }, [membershipsData]);

  const resolveCellEditorNames = useCallback(
    (taskId: string, columnId: string): readonly string[] => {
      const ids = resolveCellEditors(taskId, columnId);
      if (ids.length === 0) return EMPTY_EDITOR_NAMES;
      // メンバー一覧の取得前・退場済みメンバーは汎用ラベルで表示する。
      return ids.map((id) => memberNameById.get(id) ?? "他のユーザー");
    },
    [resolveCellEditors, memberNameById],
  );

  // ショット連動: タスク名と同名のショットがあれば Preview へ直接遷移できるようにする。
  const { data: shotsData } = useGetShotsQuery(
    {
      project_id: projectId,
      filter: "all",
      include_empty: true,
      limit: SHOT_LOOKUP_LIMIT,
    },
    {
      skip: !projectId,
      // 別タブ・別端末でショットのステータスが変わったとき、フォーカス復帰や
      // 再接続で再取得し、CB タグの表示・消滅（レンダー時判定）を追従させる。
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const shotByName = useMemo(() => {
    const map = new Map<string, Shot>();
    for (const shot of shotsData?.data?.shots || []) {
      // NFC 正規化して、Excel コピペ（NFD）と手入力（NFC）の表記ゆれを吸収する。
      const name = normalizeNfc((shot.shot_name || "").trim());
      // 同名が複数あれば先勝ち（一覧表示順）。
      if (name && !map.has(name)) map.set(name, shot);
    }
    return map;
  }, [shotsData]);

  const handleOpenShot = useCallback(
    (shotId: string) => navigate(`/${projectId}/${shotId}`),
    [navigate, projectId],
  );

  // オファー連動: タスク名とオファーの担当範囲（scope）が一致し、@表示名が
  // オファー相手（contractor）と部分一致する場合に、メンション直後へ
  // オファーステータスのバッジを表示するためのオファー一覧。
  const { data: dmData } = useGetDmThreadsQuery(
    {
      project_id: projectId,
      type: "subcontract",
      limit: OFFER_LOOKUP_LIMIT,
    },
    { skip: !projectId },
  );

  const offerThreads = useMemo(() => dmData?.data?.threads ?? [], [dmData]);

  /** タスク名 × @表示名 からオファーステータスのバッジラベルを解決する */
  const resolveMentionOfferStatus = useCallback(
    (taskTitle: string, mentionName: string) =>
      findOfferStatusLabel(offerThreads, taskTitle, mentionName),
    [offerThreads],
  );

  const [createTaskMutation] = useCreateTaskMutation();
  const [updateTaskMutation] = useUpdateTaskMutation();
  const [deleteTaskMutation] = useDeleteTaskMutation();
  const [moveTaskMutation] = useMoveTaskMutation();
  const [updateColumnsMutation] = useUpdateColumnsMutation();
  const [updateShot] = useUpdateShotMutation();

  // 各ハンドラは project_id だけを渡す。対象ボードは props 由来なので、
  // ここでまとめて board_id を差し込む（未指定なら主ボードへ向く）。
  const createTask = useCallback(
    (args: CreateTaskParams) => createTaskMutation({ ...args, board_id: boardId }),
    [createTaskMutation, boardId],
  );
  const updateTask = useCallback(
    (args: UpdateTaskParams) => updateTaskMutation({ ...args, board_id: boardId }),
    [updateTaskMutation, boardId],
  );
  const deleteTask = useCallback(
    (args: DeleteTaskParams) => deleteTaskMutation({ ...args, board_id: boardId }),
    [deleteTaskMutation, boardId],
  );
  const updateColumns = useCallback(
    (args: UpdateColumnsParams) =>
      updateColumnsMutation({ ...args, board_id: boardId }),
    [updateColumnsMutation, boardId],
  );

  const [columns, setColumns] = useState<TaskColumn[]>([]);
  const [tasks, setTasks] = useState<ProducerTask[]>([]);

  // 常に「最新の tasks」を同期的に参照できる ref。React の setState 更新関数は
  // 即時実行が保証されない（別の更新が保留中だと遅延実行される）ため、更新関数の
  // 副作用で次状態を取り出すと undefined のまま API 送信がスキップされ、
  // 「UI では更新されたのにリロードで消える」保存漏れが起きる。
  // 次状態は必ずこの ref から同期的に計算し、ref と state を同時に更新する。
  const tasksRef = useRef<ProducerTask[]>([]);
  const applyTasks = useCallback(
    // eslint-disable-next-line no-unused-vars
    (updater: (prev: ProducerTask[]) => ProducerTask[]) => {
      tasksRef.current = updater(tasksRef.current);
      setTasks(tasksRef.current);
    },
    [],
  );

  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isBulkApplying, setIsBulkApplying] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProducerTask | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [splitTarget, setSplitTarget] = useState<ProducerTask | null>(null);
  const [columnDeleteTarget, setColumnDeleteTarget] =
    useState<TaskColumn | null>(null);
  // ── 行の移動（別ボードへ） ──
  // moveTarget: 移動先選択モーダルを開いている行。
  // movePlan: 不足列の確認モーダル（移動先に無い列を追加してよいか）の内容。
  const [moveTarget, setMoveTarget] = useState<ProducerTask | null>(null);
  const [movePlan, setMovePlan] = useState<{
    task: ProducerTask;
    board: TaskMoveTargetBoard;
    missingTitles: string[];
  } | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  // ── 完了行のピン留め ──
  // 「チェックボックスが 1 個だけの行に追記したら完了扱いになり、書いた内容ごと
  // 画面から消える」のを防ぐため、この画面で完了させた行は完了フィルターを
  // 素通しさせて表示に残す（並び順も未完了群のまま = 元の位置に留める）。
  // 完了判定そのものは変えないので、フィルター切替・ボード切替・リロードで
  // 通常どおり完了行として扱われる。
  const [pinnedTaskIds, setPinnedTaskIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const pinTask = useCallback((taskId: string) => {
    setPinnedTaskIds((prev) => {
      if (prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.add(taskId);
      return next;
    });
  }, []);
  useEffect(() => {
    setPinnedTaskIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [completionFilter, boardId]);

  // 「APIキャッシュの内容ハッシュキー」が変わったときだけローカル state を上書きし、
  // 同値ならローカルの楽観的更新を温存する。これにより mutation 後の自動 refetch や
  // refetchOnFocus による再取得でもちらつかず、リアルタイム反映と編集中の整合を両立する。
  const lastBoardKeyRef = useRef<string>("");

  useEffect(() => {
    // プロジェクト / ボード切り替え時はキーを必ずリセットして初回マージを促す
    lastBoardKeyRef.current = "";
  }, [projectId, boardId]);

  useEffect(() => {
    const board = activeBoard;
    if (!board) return;
    const key = JSON.stringify({
      cols: board.columns.map((c) => [c.id, c.title, c.parallel, c.order]),
      tasks: board.tasks.map((t) => [
        t.id,
        t.title,
        t.modified,
        t.deadline,
        t.sort_order,
        t.cells,
      ]),
    });
    if (key === lastBoardKeyRef.current) return;
    lastBoardKeyRef.current = key;
    setColumns(board.columns);
    tasksRef.current = board.tasks;
    setTasks(board.tasks);
  }, [activeBoard]);

  // ── 列ヘッダーによる一時的な並び替え ──
  // 既定は「最遅日付の昇順」（sortTasksByLatestDate）。列ヘッダーの並び替え
  // ボタンで昇順 → 降順 → 既定を循環する。永続化しないため、リロードすると
  // 必ず既定の期日順に戻る。
  const [requestedSort, setRequestedSort] = useState<TaskSortState | null>(
    null,
  );

  const toggleSort = useCallback((key: string) => {
    setRequestedSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      return prev.direction === "asc" ? { key, direction: "desc" } : null;
    });
  }, []);

  // 並び替え中の列が削除された / 縦積み切替で消えた場合は既定順に戻す
  const activeSort = useMemo(() => {
    if (!requestedSort) return null;
    if (requestedSort.key === TITLE_COL_KEY) return requestedSort;
    return columns.some((c) => c.id === requestedSort.key)
      ? requestedSort
      : null;
  }, [requestedSort, columns]);

  // ── フィルタリング（担当者メンション・タスク名）+ 新しい順 ──
  // タスク名フィルターは部分一致・大文字小文字無視で照合する。
  const normalizedTaskName = taskNameFilter.trim().toLowerCase();
  // フィルター中は表示行と実データ行の index がずれるため、貼り付け・
  // タスク追加行の無効化など「編集系の抑制」にも同じフラグを使う。
  const filtering = isTaskFilterActive({
    selectedMentions,
    taskNameFilter,
    dateRange,
  });

  const visibleTasks = useMemo(() => {
    const mentionFiltered =
      selectedMentions.length > 0
        ? tasks.filter((t) => taskMatchesMentions(t, selectedMentions))
        : tasks;
    const nameFiltered = normalizedTaskName
      ? mentionFiltered.filter((t) =>
          (t.title ?? "").toLowerCase().includes(normalizedTaskName),
        )
      : mentionFiltered;
    // 日付チップ（先週 / 本日 / 明日 / 今週）の範囲で絞り込む。
    // 範囲内の日付を本文のどこにも持たないタスクは表示しない。
    const dateFiltered = dateRange
      ? nameFiltered.filter((t) => taskHasDateInRange(t, dateRange))
      : nameFiltered;
    // 完了タスクは日付に関係なく完了フィルターだけで出し分ける
    // （完了フィルターで確認できるため、当日完了分を進行中に残す特例は持たない）。
    // ピン留め中（この画面で完了させたばかり）の行はフィルターを素通しさせ、
    // 追記した内容が目の前から消えないようにする。
    const completionFiltered = dateFiltered.filter(
      (t) =>
        pinnedTaskIds.has(t.id) ||
        matchesCompletionFilter(isTaskComplete(t, columns), completionFilter),
    );
    // 既定の並び（未完了は期日昇順、完了は直近から遡る降順）を先に確定させ、
    // 列指定の並び替えは安定ソートで重ねる（同値の行は既定の順のまま残る）。
    const byDeadline = sortTasksByCompletionThenDate(
      completionFiltered,
      columns,
      dayjs(),
      pinnedTaskIds,
    );
    return activeSort
      ? sortTasksByColumn(byDeadline, activeSort, TITLE_COL_KEY)
      : byDeadline;
  }, [
    tasks,
    selectedMentions,
    normalizedTaskName,
    dateRange,
    completionFilter,
    columns,
    activeSort,
    pinnedTaskIds,
  ]);

  // ── 担当者フィルター中の列ごと非表示 ──
  // 表示対象タスクのどの行にも表示する内容が無い列は列ごと畳み、
  // 「その担当者に関係する工程」だけの表にする。表示専用の絞り込みで、
  // 列の編集操作（リネーム・削除・境界切り替え）は元の columns を対象にする。
  const displayColumns = useMemo(
    () =>
      selectedMentions.length > 0
        ? filterColumnsByMentionContent(columns, visibleTasks, selectedMentions)
        : columns,
    [columns, visibleTasks, selectedMentions],
  );

  // ── 段階描画（描画コストの制御。見える範囲の制御ではない） ──
  // データは既に全件取得済みなので、ここで絞るのは「描く行数」だけ。行数が
  // 多いボードで一度に全行を描くと重いため、末尾（最新側）から一定数だけ描き、
  // 表の上端に置いた番兵で過去へ遡って継ぎ足す（メッセージ欄と同じ読み方）。
  //
  // 「昨日まで」のような日付での足切りはしない。過去の完了行を見えなくする
  // のは描画数ではなく表の高さの役目で、そちらは bodyMaxHeight が担う。
  //
  // 列ヘッダーソート中は完了・未完了の塊構造が崩れるので、従来どおり先頭から
  // 一定数を描いて下方向へ継ぎ足す。
  const loadUpward = !activeSort;

  // 完了タスクが表に並ぶ表示（完了のみ / すべて）かどうか。空カードの畳み込み、
  // 表の高さの固定判定に使う。
  const showsCompleted = completionFilter !== "incomplete";

  // 初期描画数。既定順では末尾（最新側）から
  //   - 未完了行はすべて（ピン留め中を含む）
  //   - その手前の完了行 1 行（＝表の初期表示に収める分）
  // を最低限として必ず含め、上スクロールの初動が引っかからないよう
  // ROW_RENDER_INITIAL 行までは先に描いておく。
  const initialRenderCount = useMemo(() => {
    if (!loadUpward) return ROW_RENDER_INITIAL;
    let trailingIncomplete = 0;
    for (let i = visibleTasks.length - 1; i >= 0; i -= 1) {
      const task = visibleTasks[i];
      if (!pinnedTaskIds.has(task.id) && isTaskComplete(task, columns)) break;
      trailingIncomplete += 1;
    }
    return Math.min(
      visibleTasks.length,
      Math.max(trailingIncomplete + 1, ROW_RENDER_INITIAL),
    );
  }, [loadUpward, visibleTasks, columns, pinnedTaskIds]);

  // 上スクロールで継ぎ足したチャンク数。フィルター条件が変わったら初期化する。
  const [extraChunks, setExtraChunks] = useState(0);
  useEffect(() => {
    setExtraChunks(0);
  }, [
    completionFilter,
    selectedMentions,
    normalizedTaskName,
    dateRange,
    activeSort,
  ]);

  const renderLimit = initialRenderCount + extraChunks * ROW_RENDER_CHUNK;

  // 末尾スライスの開始位置。表示行 index → visibleTasks index の補正
  //（貼り付けアンカー）にも使う。
  const renderOffset = loadUpward
    ? Math.max(0, visibleTasks.length - renderLimit)
    : 0;
  const renderedTasks = useMemo(
    () =>
      loadUpward
        ? visibleTasks.slice(renderOffset)
        : visibleTasks.slice(0, renderLimit),
    [loadUpward, visibleTasks, renderOffset, renderLimit],
  );
  const hasMoreRows = renderedTasks.length < visibleTasks.length;

  // ── 表の中の縦スクロール（完了行の見える範囲を抑える） ──
  // 「完了」「すべて」では完了行が表の上側（過去側）に積み上がる。行を間引くと
  // 「完了も一緒に出ている」ことが伝わらず、かといって全部見せると未完了行が
  // 画面から押し出されて現在位置を見失う。そこで行は全部並べたまま、
  // 「未完了の全行 + 直近の完了 1 行」がちょうど収まる高さに表を固定し、
  // それより過去の完了は表の中を上へスクロールして遡る形にする。
  //
  // 高さは行の実測に依るので初回描画後に一度だけ測り、以降は編集で行の高さが
  // 変わっても動かさない（表が伸び縮みすると読んでいる位置を見失うため）。
  // 列ヘッダーソート中は完了・未完了の塊構造が崩れて基準行が定まらないので
  // 高さは固定しない（従来どおりページ側のスクロールに委ねる）。
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const [bodyScroll, setBodyScroll] = useState<{
    key: string;
    maxHeight: number | null;
  } | null>(null);
  // セルに貼った画像は読み込み後に行の高さが決まる（height: auto）。読み込み前に
  // 測ると高さが足りず、未完了行が初期表示からはみ出してしまうため、未読み込みの
  // 画像が残っている間は測らずに待つ。
  const [imageSettleTick, setImageSettleTick] = useState(0);

  const measureKey =
    showsCompleted && loadUpward
      ? [
          boardId,
          completionFilter,
          normalizedTaskName,
          selectedMentions.join(","),
          dateRange
            ? `${dateRange.start.format("YYYY-MM-DD")}~${dateRange.end.format("YYYY-MM-DD")}`
            : "",
        ].join("|")
      : null;
  const bodyMeasured = !measureKey || bodyScroll?.key === measureKey;
  const bodyMaxHeight =
    measureKey && bodyScroll?.key === measureKey ? bodyScroll.maxHeight : null;

  useLayoutEffect(() => {
    if (!measureKey) return;
    if (bodyScroll?.key === measureKey) return;
    const container = scrollAreaRef.current;
    if (!container) return;

    // 読み込み待ちの画像がある間は測らない。1 枚ずつ完了を拾って測り直す。
    const pendingImages = Array.from(
      container.querySelectorAll("img"),
    ).filter((img) => !img.complete);
    if (pendingImages.length > 0) {
      const bump = () => setImageSettleTick((n) => n + 1);
      pendingImages.forEach((img) => {
        img.addEventListener("load", bump, { once: true });
        img.addEventListener("error", bump, { once: true });
      });
      return () => {
        pendingImages.forEach((img) => {
          img.removeEventListener("load", bump);
          img.removeEventListener("error", bump);
        });
      };
    }

    // 基準行 = 直近の完了行。未完了の塊のひとつ手前にあたる。「完了」表示では
    // 未完了が 1 行も無いため、末尾（＝最も新しい完了）を基準にする。
    const firstIncomplete = renderedTasks.findIndex(
      (t) => pinnedTaskIds.has(t.id) || !isTaskComplete(t, columns),
    );
    const anchorIndex =
      firstIncomplete === -1 ? renderedTasks.length - 1 : firstIncomplete - 1;
    if (anchorIndex < 0) {
      // 完了行が無いので抑える必要がない
      setBodyScroll({ key: measureKey, maxHeight: null });
      return;
    }

    const row = container.querySelector<HTMLTableRowElement>(
      `tr[data-row-index="${renderOffset + anchorIndex}"]`,
    );
    if (!row) return;
    const headerHeight =
      container.querySelector("thead")?.getBoundingClientRect().height ?? 0;
    // 基準行の、スクロール内容の先頭からの位置
    const rowTop =
      row.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;
    // 列ヘッダーは固定表示で内容に重なるため、その分を足す
    const maxHeight = Math.max(
      MIN_SCROLL_BODY_HEIGHT,
      Math.round(container.scrollHeight - rowTop + headerHeight),
    );
    setBodyScroll({ key: measureKey, maxHeight });
  }, [
    measureKey,
    bodyScroll,
    renderedTasks,
    renderOffset,
    columns,
    pinnedTaskIds,
    imageSettleTick,
  ]);

  // 高さを固定した直後は最下部（＝直近の完了行と未完了行）へ寄せる
  useLayoutEffect(() => {
    if (bodyMaxHeight == null) return;
    const container = scrollAreaRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [bodyMaxHeight]);

  /** 継ぎ足しのスクロール基準。表の高さを固定中は表自身、通常はページ */
  const scrollHost = useCallback(
    (): Element | null =>
      bodyMaxHeight != null ? scrollAreaRef.current : document.scrollingElement,
    [bodyMaxHeight],
  );

  // 番兵行が見えたら次のチャンクを描く（上方向モードでは表の上端に置く）
  const loadMoreRef = useRef<HTMLTableRowElement | null>(null);
  // 上へ行を足すと下のコンテンツごと押し下げられて画面が跳ねるため、追加の
  // 直前に「スクロール要素の下端からの距離」を記録し、描画直後に復元して
  // 見えている位置を動かさない（メッセージ欄と同じアンカリング）。
  const scrollAnchorRef = useRef<number | null>(null);
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMoreRows) return;
    // 高さの実測が済む前は表全体が伸びきっており、上端の番兵も見えている。
    // ここで観測を始めると測る前に継ぎ足しが走ってしまうので待つ。
    if (!bodyMeasured) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (loadUpward) {
          // 直前の補正が済むまでは多重発火させない
          if (scrollAnchorRef.current != null) return;
          const el = scrollHost();
          if (el) scrollAnchorRef.current = el.scrollHeight - el.scrollTop;
        }
        setExtraChunks((prev) => prev + 1);
      },
      { root: bodyMaxHeight != null ? scrollAreaRef.current : null },
    );
    observer.observe(target);
    return () => observer.disconnect();
    // renderLimit を依存に含めて継ぎ足しごとに張り直す。上方向モードでは
    // 番兵自体がドキュメント上で動かないため、補正後もまだ見えている場合は
    // 張り直しによる再評価で続きを読み込ませる（見えなくなれば止まる）。
  }, [
    hasMoreRows,
    visibleTasks.length,
    loadUpward,
    renderLimit,
    bodyMeasured,
    bodyMaxHeight,
    scrollHost,
  ]);

  // 上方向の継ぎ足し後、下端からの距離を復元する（ブラウザ描画前に行うため
  // useLayoutEffect）。復元により番兵は追加分の高さだけ表示領域の上方へ
  // 押し出され、見えなくなった時点で読み込みが止まる。
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (anchor == null) return;
    scrollAnchorRef.current = null;
    const el = scrollHost();
    if (el) el.scrollTop = el.scrollHeight - anchor;
  }, [renderedTasks, scrollHost]);

  // 貼り付け・コピーで最新の表示行を参照できるようにする
  const visibleTasksRef = useRef<ProducerTask[]>(visibleTasks);
  useEffect(() => {
    visibleTasksRef.current = visibleTasks;
  }, [visibleTasks]);

  // ── 列操作（全置換 PATCH） ──
  const persistColumns = useCallback(
    async (next: TaskColumn[]) => {
      const reindexed = next.map((c, i) => ({ ...c, order: i }));
      setColumns(reindexed);
      onSaveStart?.();
      try {
        await updateColumns({
          project_id: projectId,
          columns: reindexed.map((c) => ({
            id: c.id,
            title: c.title,
            parallel: c.parallel,
          })),
        }).unwrap();
        onSaveEnd?.(true);
      } catch {
        onSaveEnd?.(false);
        toast.error("列の更新に失敗しました");
        refetch();
      }
    },
    [projectId, updateColumns, refetch, onSaveStart, onSaveEnd],
  );

  const handleRenameColumn = (columnId: string, title: string) => {
    persistColumns(
      columns.map((c) => (c.id === columnId ? { ...c, title } : c)),
    );
  };

  const handleToggleColumnParallel = (columnId: string) => {
    persistColumns(
      columns.map((c) =>
        c.id === columnId ? { ...c, parallel: !c.parallel } : c,
      ),
    );
  };

  /** 末尾に新しい列を 1 つ追加する。途中挿入は現状の UI からは行わない。 */
  const handleAddColumnEnd = () => {
    persistColumns([
      ...columns,
      {
        id: genColumnId(),
        title: "新しい列",
        order: columns.length,
        parallel: false,
      },
    ]);
  };

  /**
   * D&D で動的列の並びを入れ替える。`Table` は対象列の新順序キーのみ渡してくる
   * ため、それを元に columns を再構築して `persistColumns`（PATCH）へ渡す。
   */
  const handleReorderColumns = (orderedKeys: string[]) => {
    const byId = new Map(columns.map((c) => [c.id, c]));
    const next = orderedKeys
      .map((k) => byId.get(k))
      .filter((c): c is TaskColumn => !!c);
    if (next.length !== columns.length) return;
    persistColumns(next);
  };

  const requestDeleteColumn = (column: TaskColumn) => {
    const hasContent = tasks.some((t) =>
      (t.cells?.[column.id]?.content ?? "").trim(),
    );
    if (hasContent) {
      setColumnDeleteTarget(column);
    } else {
      doDeleteColumn(column);
    }
  };

  const doDeleteColumn = (column: TaskColumn) => {
    applyTasks((prev) =>
      prev.map((t) => {
        if (!t.cells?.[column.id]) return t;
        const nextCells = { ...t.cells };
        delete nextCells[column.id];
        return { ...t, cells: nextCells };
      }),
    );
    persistColumns(columns.filter((c) => c.id !== column.id));
    setColumnDeleteTarget(null);
  };

  // ── タスク（行）操作 ──
  const handleAddTask = useCallback(async () => {
    const title = newTaskTitle.trim();
    if (isCreating || !title) return;
    setIsCreating(true);
    onSaveStart?.();
    try {
      const res = await createTask({ project_id: projectId, title }).unwrap();
      applyTasks((prev) => [...prev, res.data.task]);
      // 「完了」表示中は追加した（未完了の）行がフィルターで消えるため画面に留める
      if (completionFilter === "complete") pinTask(res.data.task.id);
      setNewTaskTitle("");
      onSaveEnd?.(true);
      // 表の高さを固定中（「すべて」「完了」）は、追加した行と入力欄が
      // 見えるよう表の中を最下部へ送る
      if (bodyMaxHeight != null) {
        requestAnimationFrame(() => {
          const container = scrollAreaRef.current;
          if (container) container.scrollTop = container.scrollHeight;
        });
      }
    } catch {
      onSaveEnd?.(false);
      toast.error("タスクの追加に失敗しました");
    } finally {
      setIsCreating(false);
    }
  }, [
    projectId,
    newTaskTitle,
    isCreating,
    createTask,
    applyTasks,
    onSaveStart,
    onSaveEnd,
    completionFilter,
    pinTask,
    bodyMaxHeight,
  ]);

  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleAddTask();
    }
  };

  // ── チェックバック（プロデューサー待ち）ショットの自動行追加 ──
  // プロデューサー側やることリスト対象（status = PRODUCER_PENDING）のショットの
  // うち、同名タスク（NFC 正規化して照合）が無いものを自動で行追加する。
  // 追加された行はタイトル照合により CB タグ + 最新チェックバックコメントの
  // ホバー表示対象になる（TaskBoardRow / CheckbackBadge 側で判定）。
  // ボード・ショットの両方が揃った時点でプロジェクトごとに 1 回だけ実行する
  // （StrictMode の二重実行や refetch による重複作成を防ぐ）。
  const autoAddRanRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!canEdit || !projectId || !enableShotAutoAdd) return;
    if (!activeBoard || !shotsData?.data?.shots) return;
    const autoAddKey = `${projectId}:${boardId ?? ''}`;
    if (autoAddRanRef.current.has(autoAddKey)) return;
    autoAddRanRef.current.add(autoAddKey);

    const pendingShots = shotsData.data.shots.filter(
      (s) => s.status === ShotStatus.PRODUCER_PENDING,
    );
    if (pendingShots.length === 0) return;

    const existingTitles = new Set(
      tasksRef.current.map((t) => normalizeNfc((t.title || "").trim())),
    );
    const missing = pendingShots.filter((s) => {
      const name = normalizeNfc((s.shot_name || "").trim());
      return !!name && !existingTitles.has(name);
    });
    if (missing.length === 0) return;

    (async () => {
      onSaveStart?.();
      let created = 0;
      for (const shot of missing) {
        try {
          const res = await createTask({
            project_id: projectId,
            title: (shot.shot_name || "").trim(),
            // 自動追加行のマーカー（予約セルキー）。チェックバック解消時に
            // 入力の無い行を安全に自動削除するための識別子。
            cells: { [CB_AUTO_MARKER_KEY]: { content: "" } },
          }).unwrap();
          applyTasks((prev) => [...prev, res.data.task]);
          created += 1;
        } catch {
          // 個別失敗はスキップして続行（次回マウント時に再試行される）
        }
      }
      onSaveEnd?.(created === missing.length);
      if (created > 0) {
        toast.success(
          `チェックバック中のショット ${created} 件をタスクボードへ追加しました`,
        );
      }
    })();
  }, [
    canEdit,
    projectId,
    boardId,
    enableShotAutoAdd,
    activeBoard,
    shotsData,
    createTask,
    applyTasks,
    onSaveStart,
    onSaveEnd,
  ]);

  // ── チェックバック解消時のクリーンアップ ──
  // マーカー（__cb_auto）付きの自動追加行について、同名ショットのステータスが
  // PRODUCER_PENDING を外れた（= プロデューサーのやることリストから消え、
  // CB タグも消える）タイミングで次を行う。
  // - 実セルがすべて空なら行ごと削除（CB リマインダーとしての役目を終えたため）
  // - 入力があればマーカーだけ外して通常行へ昇格（以後は自動削除の対象にしない）
  // さらに、この機能より前に自動追加された既存行（マーカー無し・空・チェック
  // バック中）にはマーカーを後付けし、以後のクリーンアップ対象へ含める。
  // 同名ショットが見つからない行（ショット削除・改名・取得上限超え）は、
  // 誤削除を避けるため何もしない。
  const cbCleanupInFlightRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // 自動追加を行わないボードには __cb_auto マーカー付きの行が無いので、
    // 後始末（削除 / マーカー付け直し）も走らせない。
    if (!canEdit || !enableShotAutoAdd) return;
    if (!activeBoard || !shotsData?.data?.shots) return;

    const toDelete: ProducerTask[] = [];
    const toUnmark: ProducerTask[] = [];
    const toMark: ProducerTask[] = [];
    for (const task of tasksRef.current) {
      if (cbCleanupInFlightRef.current.has(task.id)) continue;
      const shot = shotByName.get(normalizeNfc((task.title || "").trim()));
      if (!shot) continue;
      const pending = shot.status === ShotStatus.PRODUCER_PENDING;
      const marked = hasCbAutoMarker(task);
      const empty = isTaskCellsEmpty(task);
      if (pending && !marked && empty) toMark.push(task);
      else if (!pending && marked && empty) toDelete.push(task);
      else if (!pending && marked) toUnmark.push(task);
    }
    if (!toDelete.length && !toUnmark.length && !toMark.length) return;

    const targets = [...toDelete, ...toUnmark, ...toMark];
    targets.forEach((t) => cbCleanupInFlightRef.current.add(t.id));

    (async () => {
      onSaveStart?.();
      let ok = true;
      for (const task of toDelete) {
        try {
          await deleteTask({
            project_id: projectId,
            task_id: task.id,
          }).unwrap();
          applyTasks((prev) => prev.filter((t) => t.id !== task.id));
        } catch {
          ok = false;
        }
      }
      for (const task of toUnmark) {
        const cells = withoutCbAutoMarker(task.cells || {});
        try {
          await updateTask({
            project_id: projectId,
            task_id: task.id,
            cells,
          }).unwrap();
          applyTasks((prev) =>
            prev.map((t) => (t.id === task.id ? { ...t, cells } : t)),
          );
        } catch {
          ok = false;
        }
      }
      for (const task of toMark) {
        const cells = {
          ...(task.cells || {}),
          [CB_AUTO_MARKER_KEY]: { content: "" },
        };
        try {
          await updateTask({
            project_id: projectId,
            task_id: task.id,
            cells,
          }).unwrap();
          applyTasks((prev) =>
            prev.map((t) => (t.id === task.id ? { ...t, cells } : t)),
          );
        } catch {
          ok = false;
        }
      }
      // 完了後は必ずガードを解放する。成功分は state 変化により次回の評価で
      // 対象外になり、失敗分は次のショット再取得時に再試行される。
      targets.forEach((t) => cbCleanupInFlightRef.current.delete(t.id));
      onSaveEnd?.(ok);
    })();
  }, [
    canEdit,
    projectId,
    enableShotAutoAdd,
    activeBoard,
    shotsData,
    shotByName,
    deleteTask,
    updateTask,
    applyTasks,
    onSaveStart,
    onSaveEnd,
  ]);

  /**
   * ショット連動: タスク名と同名のショットが「準備中」または「制作中」のとき、
   * タスクボードの一番右の日付（最右の日付入りセルの最遅日付）をショットの
   * 「次の納品日」へ自動反映する。成功時はトーストで通知する。
   * タスク自体の保存とは独立した後追い処理のため、保存ステータス
   * （onSaveStart/onSaveEnd）には関与させない。
   */
  const syncShotDeliveryDate = useCallback(
    async (
      task: Pick<ProducerTask, "title" | "cells">,
      // 貼り付けで列を追加した直後など、state 未反映の最新列を使いたい場合に指定
      columnsOverride?: TaskColumn[],
    ) => {
      const shot = shotByName.get(normalizeNfc((task.title || "").trim()));
      if (!shot) return;
      // 納品日を持てるのは準備中・制作中のみ（チェックバック・保留・完了は対象外）
      if (!isDeliveryDateEditable(getShotTableStatusKey(shot))) return;
      const rightmost = computeTaskRightmostDate(
        task,
        columnsOverride ?? columns,
      );
      if (!rightmost) return;
      // 既に同日なら PATCH もトーストも行わない
      if (
        shot.next_delivery_date &&
        dayjs(shot.next_delivery_date).format("YYYY-MM-DD") === rightmost
      ) {
        return;
      }
      try {
        await updateShot({
          project_id: projectId,
          shot_id: shot.id,
          // 時刻は既存の提出予定日入力のデフォルト（10:00）に合わせる
          next_delivery_date: toDeliveryDateParam(
            dayjs(`${rightmost}T10:00:00`).toDate(),
          ),
        }).unwrap();
        toast.success(`${shot.shot_name}の納品予定日を更新`);
      } catch {
        toast.error(`${shot.shot_name}の納品予定日の更新に失敗しました`);
      }
    },
    [shotByName, columns, projectId, updateShot],
  );

  const handlePatchTitle = useCallback(
    async (taskId: string, title: string) => {
      applyTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, title } : t)),
      );
      onSaveStart?.();
      try {
        await updateTask({
          project_id: projectId,
          task_id: taskId,
          title,
        }).unwrap();
        onSaveEnd?.(true);
        // タスク名変更で同名ショットと新たにリンクした場合も納品日を同期する
        const current = tasksRef.current.find((t) => t.id === taskId);
        if (current) void syncShotDeliveryDate(current);
      } catch {
        onSaveEnd?.(false);
        toast.error("更新に失敗しました");
        refetch();
      }
    },
    [
      projectId,
      updateTask,
      refetch,
      applyTasks,
      onSaveStart,
      onSaveEnd,
      syncShotDeliveryDate,
    ],
  );

  const handleChangeCell = useCallback(
    async (taskId: string, columnId: string, patch: Partial<TaskCell>) => {
      // 次状態は setState の更新関数内ではなく ref から**同期的に**計算する。
      // 更新関数の副作用に頼ると、更新が遅延実行された場合に PATCH が
      // スキップされ保存漏れになる（UI 上は反映済みに見えるため気づけない）。
      const current = tasksRef.current.find((t) => t.id === taskId);
      if (!current) return;
      const prevCell = current.cells?.[columnId] ?? { content: "" };
      const nextCell: TaskCell = { ...prevCell, ...patch };
      const nextCells = { ...current.cells, [columnId]: nextCell };
      const deadline = computeTaskLatestDate({ cells: nextCells });
      const nextTask: ProducerTask = { ...current, cells: nextCells, deadline };
      applyTasks((prev) => prev.map((t) => (t.id === taskId ? nextTask : t)));

      // 編集の結果この行が完了になったら画面に留める。追記した内容が
      // 完了フィルターで即座に消えてしまうのを防ぐ（表示は次の更新まで）。
      if (isTaskComplete(nextTask, columns)) {
        if (
          !pinnedTaskIds.has(taskId) &&
          !isTaskComplete(current, columns) &&
          completionFilter === "incomplete"
        ) {
          toast.success("完了になりました（表示は次の更新まで残ります）");
        }
        pinTask(taskId);
      }

      onSaveStart?.();
      try {
        await updateTask({
          project_id: projectId,
          task_id: taskId,
          cells: nextTask.cells,
          deadline: nextTask.deadline,
        }).unwrap();
        onSaveEnd?.(true);
        void syncShotDeliveryDate(nextTask);
      } catch {
        onSaveEnd?.(false);
        toast.error("更新に失敗しました");
        refetch();
      }
    },
    [
      projectId,
      updateTask,
      refetch,
      applyTasks,
      onSaveStart,
      onSaveEnd,
      syncShotDeliveryDate,
      columns,
      completionFilter,
      pinnedTaskIds,
      pinTask,
    ],
  );

  const handleRequestDelete = (task: ProducerTask) => {
    const hasContent = Object.values(task.cells || {}).some((c) =>
      (c?.content ?? "").trim(),
    );
    if (hasContent || task.title.trim()) {
      setDeleteTarget(task);
    } else {
      performDelete(task);
    }
  };

  const performDelete = useCallback(
    async (task: ProducerTask) => {
      setIsDeleting(true);
      onSaveStart?.();
      try {
        await deleteTask({ project_id: projectId, task_id: task.id }).unwrap();
        applyTasks((prev) => prev.filter((t) => t.id !== task.id));
        onSaveEnd?.(true);
      } catch {
        onSaveEnd?.(false);
        toast.error("削除に失敗しました");
      } finally {
        setIsDeleting(false);
        setDeleteTarget(null);
      }
    },
    [projectId, deleteTask, applyTasks, onSaveStart, onSaveEnd],
  );

  // ── 行操作（分割 / 複製） ──

  /** セル内の改行ごとに 1 行へ割る。先頭行で元の行を上書きし、残りを追加する。 */
  const performSplit = useCallback(
    async (task: ProducerTask) => {
      const rows = splitTaskByLines(task, columns);
      if (rows.length === 0) {
        setSplitTarget(null);
        return;
      }
      setIsBulkApplying(true);
      onSaveStart?.();
      try {
        const [first, ...rest] = rows;
        const updated = await updateTask({
          project_id: projectId,
          task_id: task.id,
          title: first.title,
          cells: first.cells,
          deadline: computeTaskLatestDate({ cells: first.cells }),
        }).unwrap();
        applyTasks((prev) =>
          prev.map((t) => (t.id === task.id ? updated.data.task : t)),
        );

        const created: ProducerTask[] = [];
        for (const row of rest) {
          const res = await createTask({
            project_id: projectId,
            title: row.title,
            cells: row.cells,
            deadline: computeTaskLatestDate({ cells: row.cells }),
          }).unwrap();
          created.push(res.data.task);
        }
        if (created.length > 0) {
          applyTasks((prev) => [...prev, ...created]);
        }
        onSaveEnd?.(true);
        toast.success(`${rows.length}行に分割しました`);
      } catch {
        onSaveEnd?.(false);
        toast.error("行の分割に失敗しました");
        // 途中まで反映された可能性があるのでサーバーの状態へ揃える
        lastBoardKeyRef.current = "";
        refetch();
      } finally {
        setIsBulkApplying(false);
        setSplitTarget(null);
      }
    },
    [
      columns,
      projectId,
      updateTask,
      createTask,
      applyTasks,
      refetch,
      onSaveStart,
      onSaveEnd,
    ],
  );

  const handleDuplicate = useCallback(
    async (task: ProducerTask) => {
      const cells = withoutCbAutoMarker(task.cells || {});
      onSaveStart?.();
      try {
        const res = await createTask({
          project_id: projectId,
          title: task.title,
          cells,
          deadline: computeTaskLatestDate({ cells }),
        }).unwrap();
        applyTasks((prev) => [...prev, res.data.task]);
        onSaveEnd?.(true);
        toast.success("行を複製しました");
      } catch {
        onSaveEnd?.(false);
        toast.error("行の複製に失敗しました");
      }
    },
    [projectId, createTask, applyTasks, onSaveStart, onSaveEnd],
  );

  // ── 行操作（別ボードへの移動） ──

  /**
   * 移動を実行する。成功したらこのボードのローカル state から行を除く
   * （移動先ボードはタグ無効化で再取得され、次回マージで反映される）。
   */
  const performMove = useCallback(
    async (
      task: ProducerTask,
      board: TaskMoveTargetBoard,
      addMissingColumns: boolean,
    ) => {
      setIsMoving(true);
      onSaveStart?.();
      try {
        await moveTaskMutation({
          project_id: projectId,
          board_id: boardId,
          task_id: task.id,
          target_board_id: board.id,
          add_missing_columns: addMissingColumns,
        }).unwrap();
        applyTasks((prev) => prev.filter((t) => t.id !== task.id));
        onSaveEnd?.(true);
        toast.success(`「${board.name}」へ移動しました`);
      } catch (err) {
        onSaveEnd?.(false);
        if ((err as { status?: number })?.status === 409) {
          // ローカルで照合した列構成が古かった（他端末での列変更など）。
          // 最新を取り込み直してから、もう一度確認をやり直してもらう。
          toast.error(
            "ボードの列構成が変わっていたため移動を中止しました。もう一度お試しください",
          );
          lastBoardKeyRef.current = "";
          refetch();
        } else {
          toast.error("行の移動に失敗しました");
        }
      } finally {
        setIsMoving(false);
        setMoveTarget(null);
        setMovePlan(null);
      }
    },
    [
      projectId,
      boardId,
      moveTaskMutation,
      applyTasks,
      refetch,
      onSaveStart,
      onSaveEnd,
    ],
  );

  /**
   * 移動先選択モーダルで「移動」が押された。移動先に無い列（本文があるのに
   * 対応列が無い）があれば、列を追加してよいかの確認を挟んでから実行する。
   */
  const handleSelectMoveBoard = useCallback(
    (board: TaskMoveTargetBoard) => {
      const task = moveTarget;
      if (!task) return;
      const plan = planTaskMove(task, columns, board.columns);
      if (plan.missingTitles.length > 0) {
        setMoveTarget(null);
        setMovePlan({ task, board, missingTitles: plan.missingTitles });
      } else {
        performMove(task, board, false);
      }
    },
    [moveTarget, columns, performMove],
  );

  // ── コピー / 貼り付け（Excel / CSV） ──
  const handleCopy = useCallback(
    (e: React.ClipboardEvent) => {
      const target = e.target as HTMLElement;
      // セル編集中のテキストコピーはブラウザ既定に任せる
      if (target.closest("input, textarea")) return;
      e.preventDefault();
      e.clipboardData.setData(
        "text/plain",
        buildBoardTsv(columns, visibleTasksRef.current),
      );
      toast.success("表をコピーしました");
    },
    [columns],
  );

  const applyPastedGrid = useCallback(
    async (grid: string[][], anchorIndex: number | null) => {
      let header: string[] | null = null;
      let body = grid;
      if (grid.length > 0 && isTaskHeaderRow(grid[0])) {
        header = grid[0];
        body = grid.slice(1);
      }
      body = body.filter((cells) => cells.some((c) => c !== ""));
      if (body.length === 0) return;

      // タイトル列を除いた必要列数（縦横拡張のための列数）
      const widest = Math.max(
        header ? header.length : 0,
        ...body.map((r) => r.length),
      );
      const neededDynamic = Math.max(0, widest - 1);

      setIsBulkApplying(true);
      onSaveStart?.();
      try {
        // 1. 列の確保（ヘッダーがあればラベルを反映、不足分は追加）
        let workColumns = columns.map((c) => ({ ...c }));
        let columnsChanged = false;

        if (header) {
          for (let j = 0; j < neededDynamic && j < workColumns.length; j += 1) {
            const label = (header[j + 1] ?? "").trim();
            if (label && label !== workColumns[j].title) {
              workColumns[j] = { ...workColumns[j], title: label };
              columnsChanged = true;
            }
          }
        }
        for (let j = workColumns.length; j < neededDynamic; j += 1) {
          const label = header ? (header[j + 1] ?? "").trim() : "";
          workColumns.push({
            id: genColumnId(),
            title: label || `新しい列${j + 1}`,
            order: j,
            parallel: false,
          });
          columnsChanged = true;
        }
        if (columnsChanged) {
          await persistColumns(workColumns);
        }
        const finalColumns = workColumns.map((c, i) => ({ ...c, order: i }));

        // 2. 行の反映（アンカーがあれば既存行を上書き、無ければ末尾に追加）
        const snapshot = visibleTasksRef.current;
        const createdTasks: ProducerTask[] = [];
        const tasksToSync: ProducerTask[] = [];
        let updatedCount = 0;
        let createdCount = 0;

        for (let i = 0; i < body.length; i += 1) {
          const row = body[i];
          const title = (row[0] ?? "").trim();
          const cells: Record<string, TaskCell> = {};
          finalColumns.forEach((c, j) => {
            const content = row[j + 1] ?? "";
            if (content !== "") cells[c.id] = { content };
          });

          const targetIndex = anchorIndex == null ? null : anchorIndex + i;
          const target =
            targetIndex != null && targetIndex < snapshot.length
              ? snapshot[targetIndex]
              : null;

          if (target) {
            const mergedCells = { ...target.cells, ...cells };
            const deadline = computeTaskLatestDate({ cells: mergedCells });
            const res = await updateTask({
              project_id: projectId,
              task_id: target.id,
              title: title || target.title,
              cells: mergedCells,
              deadline,
            }).unwrap();
            applyTasks((prev) =>
              prev.map((t) => (t.id === target.id ? res.data.task : t)),
            );
            tasksToSync.push(res.data.task);
            updatedCount += 1;
          } else {
            if (!title && Object.keys(cells).length === 0) continue;
            const deadline = computeTaskLatestDate({ cells });
            const res = await createTask({
              project_id: projectId,
              title,
              cells,
              deadline,
            }).unwrap();
            createdTasks.push(res.data.task);
            tasksToSync.push(res.data.task);
            createdCount += 1;
          }
        }
        if (createdTasks.length > 0) {
          applyTasks((prev) => [...prev, ...createdTasks]);
        }

        // 同名ショットの納品日同期（同一ショット名は 1 回だけ）
        const syncedNames = new Set<string>();
        for (const t of tasksToSync) {
          const key = normalizeNfc((t.title || "").trim());
          if (!key || syncedNames.has(key)) continue;
          syncedNames.add(key);
          void syncShotDeliveryDate(t, finalColumns);
        }

        const summary = [
          updatedCount > 0 ? `${updatedCount}件更新` : null,
          createdCount > 0 ? `${createdCount}件追加` : null,
        ]
          .filter(Boolean)
          .join(" / ");
        if (summary) toast.success(summary);
        onSaveEnd?.(true);
      } catch {
        onSaveEnd?.(false);
        toast.error("貼り付けの反映に失敗しました");
        // refetch でローカル state はキー比較マージから再投入される
        lastBoardKeyRef.current = "";
        refetch();
      } finally {
        setIsBulkApplying(false);
      }
    },
    [
      columns,
      projectId,
      persistColumns,
      updateTask,
      createTask,
      refetch,
      applyTasks,
      onSaveStart,
      onSaveEnd,
      syncShotDeliveryDate,
    ],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (!canEdit || isBulkApplying || filtering) return;
      // セル編集中（input/textarea）の貼り付けはブラウザ既定に任せ、
      // 表全体への一括反映（行貼り付け）にしない。
      const target = e.target as HTMLElement;
      if (target.closest("input, textarea")) return;
      const text = e.clipboardData.getData("text/plain");
      if (!text || !isTabularClipboardText(text)) return;
      e.preventDefault();
      const grid = parseClipboardTable(text);
      const rowEl = (e.target as HTMLElement).closest?.(
        "tr[data-row-index]",
      ) as HTMLElement | null;
      const anchorIndex = rowEl ? Number(rowEl.dataset.rowIndex) : null;
      applyPastedGrid(grid, anchorIndex);
    },
    [canEdit, isBulkApplying, filtering, applyPastedGrid],
  );

  // ── ラベル行右端の 3 点リーダーメニュー ──
  // 列の追加（編集権限あり時のみ）と、並列工程の縦積み ⇄ 横並びの切り替え。
  // 以前は「列を追加」専用の列と画面上部のトグルで提供していたものを畳んだ。
  const boardMenuItems = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [];
    if (canEdit) {
      items.push({
        label: "列を追加",
        icon: "add",
        onClick: handleAddColumnEnd,
      });
    }
    if (onToggleStackParallel) {
      items.push(
        stackParallel
          ? {
              label: "並列を横並び表示",
              icon: "view_column",
              onClick: onToggleStackParallel,
            }
          : {
              label: "並列を縦積み表示",
              icon: "table_rows",
              onClick: onToggleStackParallel,
            },
      );
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, stackParallel, onToggleStackParallel, columns]);

  // ── 列定義（Table 用） ──
  const tableColumns: TableColumn[] = useMemo(() => {
    // 1 列分のヘッダー（リネーム・削除・並列/直列トグル）。
    // 「自列ヘッダーで右隣との境界を切り替える」設計。右隣の列の parallel を
    // トグル対象とし、右隣が無い末尾列ではトグル非表示にする。
    // 縦積み時はこれをグループ内で縦に並べる。
    const columnHeader = (col: TaskColumn) => {
      const index = columns.findIndex((c) => c.id === col.id);
      const nextCol = columns[index + 1];
      const hasNext = !!nextCol;
      const nextParallel = nextCol ? nextCol.parallel : true;
      return (
        <TaskColumnHeader
          key={col.id}
          title={col.title}
          editable={canEdit}
          nextParallel={nextParallel}
          hasNextColumn={hasNext}
          onRename={(title) => handleRenameColumn(col.id, title)}
          onToggleNextBoundary={() => {
            if (nextCol) handleToggleColumnParallel(nextCol.id);
          }}
          onDelete={() => requestDeleteColumn(col)}
          sortDirection={
            activeSort?.key === col.id ? activeSort.direction : null
          }
          onToggleSort={() => toggleSort(col.id)}
        />
      );
    };

    // 列ラベルの背景色（アクティビティ集計の項目配色を淡くして反映）。
    // 実績のない列は undefined = 既定のグレー帯のまま。
    const headerBackground = (title: string): string | undefined => {
      const color = columnColorOf?.(title);
      return color ? mixWithWhite(color, COLUMN_LABEL_COLOR_RATIO) : undefined;
    };

    let dynamic: TableColumn[];
    if (stackParallel) {
      // 連続する並列列を 1 グループ = 1 列にまとめ、ヘッダーは縦積みにする。
      // 縦積み中は列の D&D 並び替えを無効化（グループ構造が崩れるため）。
      // 担当者フィルター中は列ごと非表示を適用済みの displayColumns から組む。
      dynamic = groupColumnsByParallel(displayColumns).map((group, groupIndex) => {
        const firstCol = group[0];
        const isParallelGroup = group.length > 1;
        // 単一列グループは th 全体を、複数列グループは各段を個別に塗る
        const singleBackground = isParallelGroup
          ? undefined
          : headerBackground(firstCol.title);
        return {
          key: `g-${firstCol.id}`,
          minWidth: 140,
          width: isParallelGroup ? 240 : 200,
          // 先頭グループ以外は必ず直列列で分割される＝直列境界。左境界線を太く描く
          headerClassName: groupIndex > 0 ? "task-col-serial" : undefined,
          headerStyle: singleBackground
            ? { background: singleBackground }
            : undefined,
          sortable: false,
          header: isParallelGroup ? (
            <StackedHeader>
              {group.map((c) => (
                <StackedHeaderItem
                  key={c.id}
                  $background={headerBackground(c.title)}
                >
                  {columnHeader(c)}
                </StackedHeaderItem>
              ))}
            </StackedHeader>
          ) : (
            columnHeader(firstCol)
          ),
        } as TableColumn;
      });
    } else {
      dynamic = displayColumns.map((col, index) => {
        const background = headerBackground(col.title);
        return {
          key: col.id,
          minWidth: 120,
          width: 200,
          // 直列（先頭以外で parallel:false）は左境界線を太く描き、依存関係を強調する
          headerClassName:
            index > 0 && !col.parallel ? "task-col-serial" : undefined,
          headerStyle: background ? { background } : undefined,
          // タイトル行をドラッグして並び替えできる対象列。固定列（タスク/追加/操作）は除外
          sortable: canEdit,
          header: columnHeader(col),
        };
      });
    }

    const titleSortDirection =
      activeSort?.key === TITLE_COL_KEY ? activeSort.direction : null;

    return [
      {
        key: TITLE_COL_KEY,
        header: (
          <>
            タスク
            {titleSortDirection && (
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 14, color: "var(--soremo-blue)" }}
              >
                {TASK_SORT_ARROW_ICON[titleSortDirection]}
              </span>
            )}
          </>
        ),
        headerTitle: "クリックでタスク名順に並び替え（昇順 → 降順 → 既定）",
        onHeaderClick: () => toggleSort(TITLE_COL_KEY),
        width: 200,
        minWidth: 140,
      },
      ...dynamic,
      {
        key: ACTIONS_COL_KEY,
        width: 40,
        minWidth: 40,
        resizable: false,
        // ラベル行右端の 3 点リーダーメニュー（列を追加 / 縦積み切り替え）
        header: boardMenuItems.length > 0 && (
          <div className="u-row-center">
            <DropdownMenu items={boardMenuItems} size="small" />
          </div>
        ),
      } as TableColumn,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, displayColumns, tasks, canEdit, stackParallel, activeSort, toggleSort, columnColorOf, boardMenuItems]);

  const columnCount = tableColumns.length;

  // 番兵行。ここが見えたら次のチャンクを描く。既定順では過去（完了）側 =
  // 表の上端、列ヘッダーソート中は従来どおり下端に置く。
  const sentinelRow = hasMoreRows ? (
    <tr ref={loadMoreRef}>
      <td colSpan={columnCount}>
        <div className="u-row-center u-ptb8">
          <span className="bodytext-13 u-text-light-gray">
            過去のタスクを読み込み中...
          </span>
        </div>
      </td>
    </tr>
  ) : null;

  /** 末尾の「タスク追加」入力行（空ボード時とテーブル末尾で共有） */
  const renderAddTaskInput = () => (
    <div className="u-row u-items-center u-gap8">
      <span
        className="material-symbols-rounded"
        style={{ fontSize: 16, color: "var(--soremo-light-gray)" }}
      >
        add
      </span>
      <input
        type="text"
        placeholder="タスク名を入力して Enter で追加"
        value={newTaskTitle}
        onChange={(e) => setNewTaskTitle(e.target.value)}
        onKeyDown={handleAddKeyDown}
        disabled={isCreating}
        style={ADD_INPUT_STYLE}
      />
    </div>
  );

  // 担当者フィルター中・未完了以外の表示中に、このプロジェクトに該当タスクが
  // 無ければセクションごと畳む（一括切り替えで空カードが並ぶのを防ぐ）。
  if ((filtering || showsCompleted) && visibleTasks.length === 0) {
    return null;
  }

  const hasBanner = !!bannerColor;
  return (
    <SectionRoot $bannerColor={bannerColor}>
      <SectionHeader $onBanner={hasBanner}>
        <div className="u-row u-items-center u-gap16" style={{ minWidth: 0 }}>
          <HeaderTitleLink
            to={`/${projectId}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${projectName}を別タブで開く`}
          >
            <h3 className="heading-13 u-line-height-200">{projectName}</h3>
            {boardLabel && (
              <span
                className={
                  hasBanner ? "bodytext-13" : "bodytext-13 u-text-light-gray"
                }
              >
                {boardLabel}
              </span>
            )}
          </HeaderTitleLink>
          {/* 下部バーと同じ導線（ホーム / トークルーム / DM / ノート / ライブラリ）。
              それぞれ別タブで開く */}
          <ProjectNavLinks
            projectId={projectId}
            position={position}
            noDeliverable={noDeliverable}
            size={18}
            onBanner={hasBanner}
          />
        </div>
        <div className="u-row u-items-center u-gap8">
          {(visibleTasks.length > 0 || filtering || showsCompleted) && (
            <span
              className={
                hasBanner ? "bodytext-13" : "bodytext-13 u-text-light-gray"
              }
            >
              {hasMoreRows
                ? `${renderedTasks.length} / ${visibleTasks.length}件`
                : filtering ||
                    showsCompleted ||
                    visibleTasks.length !== tasks.length
                  ? `${visibleTasks.length}件`
                  : `全${tasks.length}件`}
            </span>
          )}
          {canEdit && (
            <IconButton
              icon="content_copy"
              size={18}
              color={hasBanner ? "inherit" : undefined}
              title="表をコピー（Excel / スプレッドシートに貼り付け可能）"
              ariaLabel="表をコピー"
              onClick={() => {
                navigator.clipboard
                  .writeText(buildBoardTsv(columns, visibleTasksRef.current))
                  .then(() => toast.success("表をコピーしました"))
                  .catch(() => toast.error("コピーに失敗しました"));
              }}
            />
          )}
        </div>
      </SectionHeader>

      <SectionBody $onBanner={hasBanner}>
        {visibleTasks.length === 0 && !filtering ? (
          /* タスク 0 件のときは列ヘッダーも空メッセージも見せず、
             入力欄だけのシンプル UI にする（カードが軽く見えるように）。 */
          canEdit ? renderAddTaskInput() : null
        ) : (
          // 一括適用中・再取得中のローディングを重ねるだけの器（余白・枠は持たせない）
          <div className="u-w100 u-relative">
            <Table
              columns={tableColumns}
              minWidth={640}
              // 外側の SectionRoot が既に枠を持つので、表側は枠なし（二重枠を避ける）
              frame="bare"
              // 完了行の見える範囲は表の高さで抑え、過去は表の中を上へ遡る
              maxBodyHeight={bodyMaxHeight ?? undefined}
              scrollAreaRef={scrollAreaRef}
              onColumnReorder={
                canEdit && !stackParallel ? handleReorderColumns : undefined
              }
              scrollAreaProps={{
                // 表のコピー / 貼り付けを受けるためフォーカス可能にする
                tabIndex: 0,
                onCopy: handleCopy,
                onPaste: handlePaste,
              }}
            >
              {loadUpward && sentinelRow}

              {renderedTasks.map((task, index) => {
                const linkedShot = shotByName.get(
                  normalizeNfc((task.title || "").trim()),
                );
                return (
                  <TaskBoardRow
                    key={task.id}
                    task={task}
                    rowIndex={renderOffset + index}
                    projectId={projectId}
                    columns={columns}
                    displayColumns={displayColumns}
                    editable={canEdit}
                    stackParallel={stackParallel}
                    selectedMentions={selectedMentions}
                    linkedShotId={linkedShot?.id}
                    checkbackShotId={
                      linkedShot?.status === ShotStatus.PRODUCER_PENDING
                        ? linkedShot.id
                        : undefined
                    }
                    onOpenShot={handleOpenShot}
                    onPatchTitle={handlePatchTitle}
                    onChangeCell={handleChangeCell}
                    onRequestDelete={handleRequestDelete}
                    onRequestSplit={setSplitTarget}
                    onDuplicate={handleDuplicate}
                    onRequestMove={setMoveTarget}
                    canMove={moveTargetBoards.length > 0}
                    resolveMentionStatus={resolveMentionOfferStatus}
                    resolveCellEditors={resolveCellEditorNames}
                    mentionColorOf={mentionColorOf}
                  />
                );
              })}

              {!loadUpward && sentinelRow}

              {canEdit && !filtering && (
                <tr>
                  <td colSpan={columnCount}>{renderAddTaskInput()}</td>
                </tr>
              )}
            </Table>
            {(isBulkApplying || isRefreshing) && (
              <BulkOverlay>
                <SvgLoadingAnimation />
              </BulkOverlay>
            )}
          </div>
        )}

        {/* 表の右下: 最終同期時刻・同期中・保存状態の常設インジケーター */}
        <TaskBoardSyncStatus
          lastSyncedAt={lastSyncedAt}
          isFetching={isRefreshing}
          saveStatus={saveStatus}
          onRefresh={refetch}
        />
      </SectionBody>

      {/* タスク削除確認 */}
      {deleteTarget && (
        <ConfirmModal
          message={`「${deleteTarget.title || "無題のタスク"}」を削除しますか？\nこの操作は取り消せません。`}
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={() => performDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* 行分割確認 */}
      {splitTarget && (
        <ConfirmModal
          message={`「${splitTarget.title || "無題のタスク"}」をセル内の改行ごとに${splitTaskByLines(splitTarget, columns).length}行へ分割しますか？`}
          confirmLabel="分割"
          loading={isBulkApplying}
          onConfirm={() => performSplit(splitTarget)}
          onCancel={() => setSplitTarget(null)}
        />
      )}

      {/* 行移動: 移動先ボードの選択 */}
      {moveTarget && (
        <TaskMoveModal
          targetLabel={moveTarget.title}
          boards={moveTargetBoards}
          loading={isMoving}
          onMove={handleSelectMoveBoard}
          onClose={() => setMoveTarget(null)}
        />
      )}

      {/* 行移動: 移動先に無い列を追加してよいかの確認 */}
      {movePlan && (
        <ConfirmModal
          message={`${movePlan.missingTitles.map((t) => `「${t}」`).join("")}列を移動先に追加してよろしいですか？`}
          confirmLabel="追加して移動"
          loading={isMoving}
          onConfirm={() => performMove(movePlan.task, movePlan.board, true)}
          onCancel={() => setMovePlan(null)}
        />
      )}

      {/* 列削除確認 */}
      {columnDeleteTarget && (
        <ConfirmModal
          message={`列「${columnDeleteTarget.title}」を削除しますか？\nこの列に入力された各タスクの内容も削除されます。`}
          confirmLabel="削除"
          onConfirm={() => doDeleteColumn(columnDeleteTarget)}
          onCancel={() => setColumnDeleteTarget(null)}
        />
      )}
    </SectionRoot>
  );
};

export default TaskBoardSection;
